import React from 'react'
import { Building2, MapPin, Phone, Mail, User, CreditCard, Star } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  Badge,
} from '../ui'
import type { Organization } from '../../types/organization.types'
import { APP_TIME_ZONE } from '@/utils/dateFormat'

interface OrganizationCardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization: Organization | null
}

export function OrganizationCard({
  open,
  onOpenChange,
  organization
}: OrganizationCardProps) {
  if (!organization) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                {organization.name}
                {organization.is_headquarters && (
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                )}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {organization.legal_name || 'Полное наименование не указано'}
              </p>
            </div>
          </div>
          <DialogClose onClose={() => onOpenChange(false)} />
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Status badges */}
          <div className="flex items-center gap-2">
            {organization.is_headquarters ? (
              <Badge variant="default">Головная организация</Badge>
            ) : (
              <Badge variant="outline">Дочерняя организация</Badge>
            )}
            {organization.is_active ? (
              <Badge variant="secondary" className="bg-green-100 text-green-800">Активна</Badge>
            ) : (
              <Badge variant="secondary" className="bg-red-100 text-red-800">Неактивна</Badge>
            )}
            {organization.code_1c && (
              <Badge variant="secondary">1С: {organization.code_1c}</Badge>
            )}
          </div>

          {/* Main info */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Основная информация</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">БИН</span>
                <p className="text-sm font-mono font-medium">{organization.bin}</p>
              </div>
              {organization.director_name && (
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="text-xs text-gray-500">Директор</span>
                    <p className="text-sm">{organization.director_name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Smart Schedule — Forecast API mapping */}
          {(organization.forecast_department_id ||
            organization.forecast_department_name) && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="text-sm font-medium text-gray-900">
                Smart Schedule (прогноз продаж)
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {organization.forecast_department_name && (
                  <div>
                    <span className="text-xs text-gray-500">Название отдела</span>
                    <p className="text-sm">{organization.forecast_department_name}</p>
                  </div>
                )}
                {organization.forecast_department_id && (
                  <div>
                    <span className="text-xs text-gray-500">UUID в forecast API</span>
                    <p className="text-xs font-mono break-all">
                      {organization.forecast_department_id}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact info */}
          {(organization.phone || organization.email) && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="text-sm font-medium text-gray-900">Контакты</h3>

              <div className="grid grid-cols-2 gap-4">
                {organization.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{organization.phone}</span>
                  </div>
                )}
                {organization.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{organization.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Addresses */}
          {(organization.legal_address || organization.actual_address) && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="text-sm font-medium text-gray-900">Адреса</h3>

              <div className="space-y-3">
                {organization.legal_address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-xs text-gray-500">Юридический адрес</span>
                      <p className="text-sm">{organization.legal_address}</p>
                    </div>
                  </div>
                )}
                {organization.actual_address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-xs text-gray-500">Фактический адрес</span>
                      <p className="text-sm">{organization.actual_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bank details */}
          {(organization.bank_name || organization.bank_account || organization.bik) && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Банковские реквизиты
              </h3>

              <div className="grid grid-cols-3 gap-4">
                {organization.bank_name && (
                  <div>
                    <span className="text-xs text-gray-500">Банк</span>
                    <p className="text-sm">{organization.bank_name}</p>
                  </div>
                )}
                {organization.bank_account && (
                  <div>
                    <span className="text-xs text-gray-500">ИИК</span>
                    <p className="text-sm font-mono">{organization.bank_account}</p>
                  </div>
                )}
                {organization.bik && (
                  <div>
                    <span className="text-xs text-gray-500">БИК</span>
                    <p className="text-sm font-mono">{organization.bik}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Statistics */}
          {organization._count && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 mb-3">Статистика</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {organization._count.employees || 0}
                  </p>
                  <p className="text-xs text-blue-700">Сотрудников</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {organization._count.departments || 0}
                  </p>
                  <p className="text-xs text-blue-700">Отделов</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {organization._count.positions || 0}
                  </p>
                  <p className="text-xs text-blue-700">Должностей</p>
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-gray-500 flex justify-between">
            <span>Создано: {formatDate(organization.created_at)}</span>
            <span>Обновлено: {formatDate(organization.updated_at)}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
